<template>
  <div class="container">
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title>Configuración de Circuitos de Estudios</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="nuevoCircuito">
          <v-icon left>mdi-plus</v-icon>
          Nuevo Circuito
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-data-table
          v-if="circuitos.length"
          :headers="headers"
          :items="circuitos"
          class="elevation-1"
          locale="es-ar"
          item-key="id"
          :disable-sort="true"
          :items-per-page="10"
          :footer-props="{
            itemsPerPageText: 'Filas por página',
          }"
        >
          <template v-slot:item.activo="{ item }">
            <v-chip
              small
              :color="item.activo ? 'green' : 'grey'"
              dark
            >
              {{ item.activo ? "Activo" : "Inactivo" }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  medium
                  class="mr-3"
                  color="blue"
                  v-bind="attrs"
                  v-on="on"
                  @click="editarCircuito(item)"
                >mdi-pencil</v-icon>
              </template>
              <span>Editar Circuito</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  medium
                  class="mr-3"
                  :color="item.activo ? 'red' : 'green'"
                  v-bind="attrs"
                  v-on="on"
                  @click="toggleActivoCircuito(item)"
                >{{ item.activo ? "mdi-close-circle" : "mdi-check-circle" }}</v-icon>
              </template>
              <span>{{ item.activo ? "Desactivar" : "Activar" }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
        <v-alert class="mt-4" color="purple" dark outlined v-else>
          No hay circuitos configurados para la institución.
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="error" @click="salir">Salir</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialog para crear/editar circuito -->
    <v-dialog v-model="dialogForm" persistent max-width="800px" transition="dialog-bottom-transition">
      <v-form ref="formCircuito" v-model="validForm" :lazy-validation="lazy">
        <v-card>
          <v-toolbar dark color="primary">
            <v-btn icon dark @click="cerrarDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ editando ? 'Editar Circuito' : 'Nuevo Circuito' }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="formCircuito.nombre"
                  label="Nombre *"
                  :rules="requiredRules"
                  dense
                  prepend-icon="mdi-rename-box"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="formCircuito.periodicidadDias"
                  label="Periodicidad (días) *"
                  :rules="periodicidadRules"
                  type="number"
                  min="1"
                  dense
                  prepend-icon="mdi-calendar-refresh"
                  @keypress="onlyNumber"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="formCircuito.sexoAplica"
                  :items="opcionesSexo"
                  label="Sexo Aplica"
                  dense
                  prepend-icon="mdi-gender-male-female"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="formCircuito.edadMinima"
                  label="Edad Mínima"
                  type="number"
                  min="0"
                  dense
                  prepend-icon="mdi-account-child"
                  @keypress="onlyNumber"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="formCircuito.edadMaxima"
                  label="Edad Máxima"
                  type="number"
                  min="0"
                  dense
                  prepend-icon="mdi-account"
                  @keypress="onlyNumber"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="formCircuito.diasAnticipacionDashboard"
                  label="Días Anticipación Dashboard *"
                  :rules="diasAnticipacionRules"
                  type="number"
                  min="1"
                  dense
                  prepend-icon="mdi-bell-ring"
                  @keypress="onlyNumber"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-switch
                  v-model="formCircuito.notificarPaciente"
                  label="Notificar Paciente"
                  dense
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="4">
                <v-switch
                  v-model="formCircuito.activo"
                  label="Activo"
                  dense
                ></v-switch>
              </v-col>
            </v-row>

            <!-- Sección de Estudios del Circuito -->
            <v-divider class="my-4"></v-divider>

            <v-alert v-if="estudiosProtegidos" type="warning" dense outlined class="mb-3">
              <strong>Estudios protegidos:</strong> Hay pacientes con turnos asociados a los estudios de este circuito. Solo se permite editar los campos de configuración. Para modificar los estudios, primero elimine los pacientes del circuito desde el Dashboard.
            </v-alert>

            <v-row align="center" class="mb-2">
              <v-col>
                <span class="subtitle-1 font-weight-bold">
                  <v-icon left color="primary">mdi-clipboard-list</v-icon>
                  Estudios del Circuito
                </span>
              </v-col>
              <v-col class="text-right">
                <v-btn small color="primary" @click="agregarEstudio" :disabled="estudiosProtegidos">
                  <v-icon left small>mdi-plus</v-icon>
                  Agregar Estudio
                </v-btn>
              </v-col>
            </v-row>

            <v-simple-table v-if="formCircuito.estudios.length" dense class="estudios-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center" style="width: 50px">#</th>
                    <th style="width: 80px">Orden</th>
                    <th>Tipo de Turno (opcional)</th>
                    <th>Nombre</th>
                    <th class="text-center" style="width: 60px">Eliminar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(estudio, index) in formCircuito.estudios" :key="index">
                    <td class="text-center">{{ index + 1 }}</td>
                    <td>
                      <v-btn icon x-small :disabled="index === 0 || estudiosProtegidos" @click="moverEstudioArriba(index)">
                        <v-icon small>mdi-arrow-up</v-icon>
                      </v-btn>
                      <v-btn icon x-small :disabled="index === formCircuito.estudios.length - 1 || estudiosProtegidos" @click="moverEstudioAbajo(index)">
                        <v-icon small>mdi-arrow-down</v-icon>
                      </v-btn>
                    </td>
                    <td>
                      <v-autocomplete
                        v-model="estudio.idTipoTurno"
                        :items="tiposTurnoItems"
                        item-text="nombreTipoTurno"
                        item-value="idTipoTurno"
                        dense
                        hide-details
                        clearable
                        :disabled="estudiosProtegidos"
                        class="my-1"
                      ></v-autocomplete>
                    </td>
                    <td>
                      <v-text-field
                        v-model="estudio.nombre"
                        dense
                        hide-details
                        :rules="requiredRules"
                        :disabled="estudiosProtegidos"
                        class="my-1"
                      ></v-text-field>
                    </td>
                    <td class="text-center">
                      <v-btn icon small color="red" @click="eliminarEstudio(index)" :disabled="estudiosProtegidos">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <v-alert v-else type="info" dense outlined class="mt-2">
              No hay estudios agregados. Agregue al menos un estudio al circuito.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" :disabled="!validForm" @click="guardarCircuito">Guardar</v-btn>
            <v-btn color="error" @click="cerrarDialog">Cancelar</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<style scoped>
::v-deep .v-data-table__empty-wrapper {
  display: none;
}
.estudios-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.estudios-table th {
  background-color: #f5f5f5 !important;
}
</style>

<script>
import axios from "../../axios-auth.js";
import router from "../../router/index.js";

export default {
  data() {
    return {
      circuitos: [],
      headers: [
        { text: "Nombre", value: "nombre" },
        { text: "Periodicidad (días)", value: "periodicidadDias" },
        { text: "Estado", value: "activo" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      // Dialog form
      dialogForm: false,
      editando: false,
      editandoId: null,
      validForm: false,
      lazy: false,
      formCircuito: {
        nombre: "",
        periodicidadDias: null,
        sexoAplica: "Todos",
        edadMinima: null,
        edadMaxima: null,
        notificarPaciente: true,
        diasAnticipacionDashboard: 30,
        activo: true,
        estudios: [],
      },
      opcionesSexo: ["Masculino", "Femenino", "Todos"],
      tiposTurnoItems: [],
      estudiosProtegidos: false,
      requiredRules: [(v) => !!v || "Dato obligatorio"],
      periodicidadRules: [
        (v) => !!v || "Dato obligatorio",
        (v) => parseInt(v) > 0 || "Debe ser mayor a 0",
      ],
      diasAnticipacionRules: [
        (v) => !!v || "Dato obligatorio",
        (v) => parseInt(v) > 0 || "Debe ser mayor a 0",
      ],
    };
  },
  methods: {
    cargarCircuitos() {
      let self = this;
      axios
        .get("/circuitos")
        .then((response) => {
          if (response.data != null && response.data != "")
            self.circuitos = response.data;
          else self.circuitos = [];
        })
        .catch((error) => {
          self.showAlert({
            icon: "error",
            title: "Error al cargar los circuitos",
            vm: self,
          });
        });
    },
    nuevoCircuito() {
      this.editando = false;
      this.editandoId = null;
      this.estudiosProtegidos = false;
      this.formCircuito = {
        nombre: "",
        periodicidadDias: null,
        sexoAplica: "Todos",
        edadMinima: null,
        edadMaxima: null,
        notificarPaciente: true,
        diasAnticipacionDashboard: 30,
        activo: true,
        estudios: [],
      };
      this.cargarTiposTurno();
      this.dialogForm = true;
      this.$nextTick(() => {
        if (this.$refs.formCircuito) this.$refs.formCircuito.resetValidation();
      });
    },
    editarCircuito(item) {
      this.editando = true;
      this.editandoId = item.id;
      this.estudiosProtegidos = false;
      this.formCircuito = {
        nombre: item.nombre,
        periodicidadDias: item.periodicidadDias,
        sexoAplica: item.sexoAplica || "Todos",
        edadMinima: item.edadMinima,
        edadMaxima: item.edadMaxima,
        notificarPaciente: item.notificarPaciente,
        diasAnticipacionDashboard: item.diasAnticipacionDashboard || 30,
        activo: item.activo,
        estudios: item.estudios
          ? item.estudios.map((e) => ({
              id: e.id,
              idTipoTurno: e.idTipoTurno,
              nombre: e.nombre,
              orden: e.orden,
            }))
          : [],
      };
      this.cargarTiposTurno();
      // Verificar si tiene pacientes asociados a los estudios
      axios
        .get("/circuitos/" + item.id + "/tiene-pacientes-asociados")
        .then((response) => {
          if (response.data && response.data.tienePacientesAsociados) {
            this.estudiosProtegidos = true;
          }
        })
        .catch(() => {});
      this.dialogForm = true;
      this.$nextTick(() => {
        if (this.$refs.formCircuito) this.$refs.formCircuito.resetValidation();
      });
    },
    cerrarDialog() {
      this.dialogForm = false;
      if (this.$refs.formCircuito) this.$refs.formCircuito.resetValidation();
    },
    guardarCircuito() {
      if (!this.$refs.formCircuito.validate()) return;

      // Validar que al menos un estudio esté agregado si el circuito se activa
      if (this.formCircuito.activo && this.formCircuito.estudios.length === 0) {
        this.showAlert({
          icon: "error",
          title: "Debe agregar al menos un estudio para activar el circuito",
          vm: this,
        });
        return;
      }

      // Validar que edadMinima <= edadMaxima si ambas están definidas
      if (
        this.formCircuito.edadMinima != null &&
        this.formCircuito.edadMaxima != null &&
        this.formCircuito.edadMinima !== "" &&
        this.formCircuito.edadMaxima !== "" &&
        parseInt(this.formCircuito.edadMinima) > parseInt(this.formCircuito.edadMaxima)
      ) {
        this.showAlert({
          icon: "error",
          title: "La edad mínima no puede ser mayor que la edad máxima",
          vm: this,
        });
        return;
      }

      let self = this;
      const circuitoData = {
        nombre: this.formCircuito.nombre,
        periodicidadDias: parseInt(this.formCircuito.periodicidadDias),
        sexoAplica: this.formCircuito.sexoAplica,
        edadMinima: this.formCircuito.edadMinima ? parseInt(this.formCircuito.edadMinima) : null,
        edadMaxima: this.formCircuito.edadMaxima ? parseInt(this.formCircuito.edadMaxima) : null,
        notificarPaciente: this.formCircuito.notificarPaciente,
        diasAnticipacionDashboard: parseInt(this.formCircuito.diasAnticipacionDashboard),
        activo: this.formCircuito.activo,
        estudios: this.formCircuito.estudios.map((e, idx) => ({
          id: e.id || 0,
          idTipoTurno: e.idTipoTurno || 0,
          nombre: e.nombre,
          orden: idx + 1,
        })),
      };

      if (this.editando) {
        circuitoData.id = this.editandoId;
        axios
          .put("/circuitos/" + this.editandoId, circuitoData)
          .then((response) => {
            self.showAlert({
              icon: "success",
              title: "Circuito modificado correctamente",
              vm: self,
            });
            self.cerrarDialog();
            self.cargarCircuitos();
          })
          .catch((error) => {
            self.showAlert({
              icon: "error",
              title: "No se pudo modificar el circuito",
              vm: self,
            });
          });
      } else {
        axios
          .post("/circuitos", circuitoData)
          .then((response) => {
            self.showAlert({
              icon: "success",
              title: "Circuito creado correctamente",
              vm: self,
            });
            self.cerrarDialog();
            self.cargarCircuitos();
          })
          .catch((error) => {
            self.showAlert({
              icon: "error",
              title: "No se pudo crear el circuito",
              vm: self,
            });
          });
      }
    },
    cargarTiposTurno() {
      let self = this;
      axios
        .get("/Profesional/GetTiposTurno", {
          params: {
            idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
          },
        })
        .then((response) => {
          self.tiposTurnoItems = response.data || [];
        })
        .catch((error) => {
          self.tiposTurnoItems = [];
        });
    },
    agregarEstudio() {
      this.formCircuito.estudios.push({
        id: 0,
        idTipoTurno: null,
        nombre: "",
        orden: this.formCircuito.estudios.length + 1,
      });
    },
    eliminarEstudio(index) {
      this.formCircuito.estudios.splice(index, 1);
    },
    moverEstudioArriba(index) {
      if (index === 0) return;
      const estudios = this.formCircuito.estudios;
      const temp = estudios[index];
      this.$set(estudios, index, estudios[index - 1]);
      this.$set(estudios, index - 1, temp);
    },
    moverEstudioAbajo(index) {
      const estudios = this.formCircuito.estudios;
      if (index === estudios.length - 1) return;
      const temp = estudios[index];
      this.$set(estudios, index, estudios[index + 1]);
      this.$set(estudios, index + 1, temp);
    },
    onlyNumber($event) {
      let keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if (keyCode < 48 || keyCode > 57) {
        $event.preventDefault();
      }
    },
    toggleActivoCircuito(item) {
      const accion = item.activo ? "Desactivar" : "Activar";

      // Validar que tenga al menos un estudio para poder activar
      if (!item.activo && (!item.estudios || item.estudios.length === 0)) {
        this.showAlert({
          icon: "error",
          title: "No se puede activar un circuito sin estudios asociados",
          vm: this,
        });
        return;
      }

      this.$swal({
        title: `${accion} Circuito`,
        text: `¿Desea ${accion.toLowerCase()} el circuito "${item.nombre}"?`,
        icon: "warning",
        showCancelButton: true,
        background: "#ececec",
        confirmButtonText: "Si",
        cancelButtonText: "No",
        showCloseButton: true,
        showLoaderOnConfirm: true,
      }).then((result) => {
        if (result.value) {
          let self = this;
          const circuitoData = {
            id: item.id,
            nombre: item.nombre,
            periodicidadDias: item.periodicidadDias,
            sexoAplica: item.sexoAplica || "Todos",
            edadMinima: item.edadMinima,
            edadMaxima: item.edadMaxima,
            notificarPaciente: item.notificarPaciente,
            diasAnticipacionDashboard: item.diasAnticipacionDashboard || 30,
            activo: !item.activo,
            estudios: item.estudios
              ? item.estudios.map((e) => ({
                  id: e.id || 0,
                  idTipoTurno: e.idTipoTurno,
                  nombre: e.nombre,
                  orden: e.orden,
                }))
              : [],
          };
          axios
            .put("/circuitos/" + item.id, circuitoData)
            .then((response) => {
              self.showAlert({
                icon: "success",
                title: `Circuito ${accion.toLowerCase()}do correctamente`,
                vm: self,
              });
              self.cargarCircuitos();
            })
            .catch((error) => {
              self.showAlert({
                icon: "error",
                title: `No se pudo ${accion.toLowerCase()} el circuito`,
                vm: self,
              });
            });
        }
      });
    },
    salir() {
      router.push({ path: "/Home" });
    },
    showAlert(alertInfo) {
      alertInfo.vm.$swal.fire({
        icon: alertInfo.icon,
        title: alertInfo.title,
        showConfirmButton: false,
        background: "#ececec",
        timer: 2500,
      });
    },
  },
  created() {
    this.cargarCircuitos();
  },
};
</script>
